interface GlobalLanguage {
  country: string;
  htmlTag_langKey: string;
  language: string;
  locale_all: string;
  sys_language_uid: string;
  href_lang: string;
}

declare global {
  interface Window {
    languages: GlobalLanguage[];
  }
}

export const globalLanguages: GlobalLanguage[] = window.languages;

export type MappedGlobalLanguage = {
  languageUid: number;
  languageCode: string;
  countryCode: string;
  locale: string;
  href_lang: string;
};

const getLanguages = (): {
  [languageUid: string]:
    | MappedGlobalLanguage
    | undefined;
} => {
  return globalLanguages.reduce(
    (map, { sys_language_uid, language, country, href_lang }) => ({
      ...map,
      [sys_language_uid]: {
        languageUid: parseInt(sys_language_uid, 10),
        languageCode: language,
        countryCode: country.toUpperCase(),
        locale: `${language}-${country.toUpperCase()}`,
        href_lang: href_lang
      }
    }),
    {}
  );
};

const getLanguageCodes = (): string[] => {
  return globalLanguages.map(({ language }) => language);
};

export const languages = getLanguages();

export const languageCodes = getLanguageCodes();

const vehicleCenterCountryCodes = [
  'FR',
  'GR',
  'BE',
  'GB',
  'CH',
  'DK',
  'PT',
  'ES',
  'IT',
  'DE',
  'NL',
  'FR',
  'CZ',
  'GR',
  'SE',
  'PL',
  'AT',
  'SK',
  'HU',
  'LV',
  'SI',
  'HR',
  'US',
  'IE',
  'MT'
];

// #38485 fallback for languages
const languageMap: {
  [language: string]: { language: string; country: string };
} = {
  cy: {
    language: 'uk',
    country: 'GB'
  },
  ga: {
    language: 'uk',
    country: 'IE'
  },
  gd: {
    language: 'uk',
    country: 'GB'
  },
  mt: {
    language: 'en',
    country: 'MT'
  }
};

const getBrowserLocale = () => {
  const browserLanguages =
    navigator.languages && navigator.languages.length > 0
      ? navigator.languages
      : [navigator.language];

  const browserLocales = browserLanguages.map(locale => locale.split('-'));

  const globalLocales = Object.keys(languages).map(k => languages[k]);

  const globalLanguages = globalLocales
    .map(locale => (locale ? locale.languageCode : ''))
    .filter(Boolean);

  const globalLanguage2CountryMap: {
    [languageCode: string]: string;
  } = globalLocales.reduce(
    (acc, locale) => ({
      ...acc,
      ...(locale ? { [locale.languageCode]: locale.countryCode } : {})
    }),
    {}
  );

  // iterate over the browser's locales to find the best match for each locale
  return (
    browserLocales
      .map(([browserLanguageCode, browserCountryCode]) => {
        // look for the browser's language-code in the languages-data provided by Typo3
        const languageCode: false | string =
          browserLanguageCode in languageMap
            ? languageMap[browserLanguageCode].language
            : globalLanguages.includes(browserLanguageCode) &&
              browserLanguageCode;
        // look for the browser's country-code in the static list of country-codes with
        // vehicle-center or in the languages-data of Typo3
        const countryCode: false | string =
          browserLanguageCode in languageMap
            ? // use EU countryCode for 'en' language without a countryCode so GB isn't used as it has it's own version, de-DE as default when no fitting language or country is found in browserLanguage and in languageMap list
              languageMap[browserLanguageCode].country
            : (vehicleCenterCountryCodes.includes(browserCountryCode) &&
              browserCountryCode) ||
              (browserLanguageCode === 'en' && 'EU') ||
              (browserLanguageCode in globalLanguage2CountryMap &&
                globalLanguage2CountryMap[browserLanguageCode]);
        // if we found both, a matching languageCode and countryCode, return it
        if (languageCode && countryCode) {
          return {
            languageCode,
            countryCode
          };
        }
        // otherwise return null
        return null;
      })
      // filter out `null`s and return the first match or the fallback default
      .filter(Boolean)[0] || { languageCode: 'de', countryCode: 'DE' }
  );
};

const { languageCode, countryCode } = getBrowserLocale();

export const userLanguage = languageCode;

export const userCountry = countryCode;

export const userLocale = `${languageCode}-${countryCode}`;

export const fallbackLanguageUid = 0; // DE

const maybeFallbackLanguage = languages[fallbackLanguageUid];

if (!maybeFallbackLanguage) {
  throw new Error('Invalid fallbackLanguage!');
}

export const fallbackLanguage = maybeFallbackLanguage;
